'use client';

import { useLocation, useNavigate } from 'react-router-dom';

import { AppAccountSidebar } from '@frontend/App/Account/App.AccountSidebar';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@frontend/components/ui/sidebar';
import { cn } from '@frontend/lib/utils';
import { useIsAdmin } from '@frontend/shared/hooks/shared.hook.useIsAdmin';
import { useIsEditor } from '@frontend/shared/hooks/shared.hook.useIsEditor';
import { BookOpen, Home, Settings, X } from 'lucide-react';

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditor = useIsEditor();
  const isAdmin = useIsAdmin();
  const { toggleSidebar } = useSidebar();

  // Determine user role for navigation
  const showAdminItems = isAdmin || isEditor;

  // Navigation items configuration
  const topMenuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      path: '/dashboard',
      visible: showAdminItems,
    },
    {
      id: 'answerbank',
      label: 'Answer Bank',
      icon: BookOpen,
      path: '/questions',
      visible: true, // All roles
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: '/settings',
      visible: showAdminItems,
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === '/questions') {
      // Answer Bank is active for /questions and root path
      return location.pathname === '/questions' || location.pathname === '/';
    }
    return location.pathname === path;
  };

  return (
    <Sidebar
      collapsible="offcanvas"
      variant="sidebar"
      className="border-r"
      side="left"
    >
      <SidebarHeader className="h-16 border-b bg-background/50">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center gap-3">
            {/* Logo */}
            <div className="flex h-9 w-9 items-center justify-center rounded-lg overflow-hidden shadow-sm">
              <img
                src="/assets/apple-touch-icon-180x180.png"
                alt="QuestionBase"
                className="h-full w-full object-cover"
              />
            </div>
            {/* Brand name */}
            <span className="font-semibold text-base text-foreground">
              Question Base
            </span>
          </div>
          {/* Close X button */}
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-accent/80 rounded-lg transition-all duration-200 hover:scale-105"
            aria-label="Close menu"
          >
            <X className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent className="py-4">
        {/* Top navigation items */}
        <SidebarGroup className="px-0">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {topMenuItems
                .filter((item) => item.visible)
                .map((item) => (
                  <SidebarMenuItem key={item.id} className="relative pl-4">
                    {/* Edge-to-edge active background */}
                    {isActive(item.path) && (
                      <div className="absolute inset-0 bg-primary/10 border-r-4 border-primary" />
                    )}
                    <SidebarMenuButton
                      isActive={isActive(item.path)}
                      onClick={() => handleNavigation(item.path)}
                      className={cn(
                        'h-11 px-4 font-medium transition-all duration-200 relative z-10',
                        'hover:bg-accent/50 rounded-none justify-start',
                        isActive(item.path)
                          ? 'text-primary bg-transparent font-semibold'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="ml-3">{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Account in footer */}
      <SidebarFooter className="border-t bg-background/30 px-4 py-3">
        <SidebarGroup className="px-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <AppAccountSidebar />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  );
}
